import React, { memo, useCallback } from 'react';
import { AppBar, Toolbar, IconButton, Box, Typography, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { Menu as MenuIcon, MoveUp, TableRows as TableRowsIcon, ViewHeadline } from '@mui/icons-material';
import Logo from './Logo';
import UserMenu from './UserMenu';
import UserAvatar from './UserAvatar';
import NavigationIcons from './NavigationIcons';

interface TopBarProps {
  isMobile?: boolean;
  showLogo?: boolean;
  showNavSwitch?: boolean;
  sidebarWidth?: number;
  title?: string;
  onDrawerToggle?: () => void;
  onNavPositionChange?: () => void;
  userMenuAnchor: HTMLElement | null;
  onUserMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onUserMenuClose: () => void;
}

const TopBar = memo<TopBarProps>(
  ({
    isMobile = false,
    showLogo = true,
    showNavSwitch = true,
    sidebarWidth = 0,
    title,
    onDrawerToggle,
    onNavPositionChange,
    userMenuAnchor,
    onUserMenuOpen,
    onUserMenuClose
  }) => {
    const theme = useTheme();

    const getAppBarStyles = useCallback(() => {
      if (isMobile) {
        return {
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        };
      }

      return {
        width: `calc(100% - ${sidebarWidth}px)`,
        ml: `${sidebarWidth}px`,
        zIndex: theme.zIndex.drawer - 1,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard
        })
      };
    }, [isMobile, sidebarWidth, theme]);

    const renderLeftSection = useCallback(() => {
      if (isMobile) {
        return (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onDrawerToggle}
              sx={{
                mr: 2,
                color: theme.palette.primary.main
              }}
            >
              <MenuIcon />
            </IconButton>
            {showLogo && <Logo variant="mobile" sx={{ flexGrow: 1 }} />}
          </>
        );
      }
      return null;
    }, [isMobile, onDrawerToggle, showLogo, theme.palette.primary.main]);

    const renderCenterSection = useCallback(() => {
      if (!isMobile && title) {
        return (
          <Typography
            variant="h6"
            component="h1"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            {title}
          </Typography>
        );
      }
      return <Box sx={{ flexGrow: 1 }} />;
    }, [isMobile, title, theme.palette.text.primary]);

    const renderRightSection = useCallback(
      () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showNavSwitch && !isMobile && (
            <Tooltip title="Switch to top navigation">
              <IconButton
                onClick={onNavPositionChange}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' }
                }}
              >
                <MoveUp />
              </IconButton>
            </Tooltip>
          )}

          <NavigationIcons showHelp={false} showNotifications={true} showThemeToggle={true} variant="default" />
          <UserAvatar onClick={onUserMenuOpen} />
        </Box>
      ),
      [showNavSwitch, isMobile, onNavPositionChange, onUserMenuOpen, theme.palette.primary.main]
    );

    return (
      <AppBar position="fixed" elevation={0} sx={getAppBarStyles()}>
        <Toolbar
          sx={{
            minHeight: '62px!important',
            justifyContent: 'space-between'
          }}
        >
          {renderLeftSection()}
          {renderCenterSection()}
          {renderRightSection()}
        </Toolbar>

        <UserMenu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={onUserMenuClose} />
      </AppBar>
    );
  }
);

TopBar.displayName = 'TopBar';

export default TopBar;
